import type { FC, MouseEvent } from "react";
import { useEffect } from "react";

import { Dialog } from "@ui/components/Dialog";
import { useViewport } from "@ui/hooks/useViewport";
import classNames from "classnames";
import DomPurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import styles from "./LoyaltyReminderDialog.module.scss";

const { sanitize } = DomPurify;

const BREAKPOINT_SMALL_LARGE: number = 768;
const SECTION = "cart - pop up";
const CTA_LOCATION = SECTION;
const CTA_NAME_POPUP_CLOSE = "pop up close";

const buildCommonTrackingData = () => {
  return {
    section: SECTION,
  };
};

const dispatchLoyaltyReminderCtaClick = (ctaName: string) => {
  dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
    ...buildCommonTrackingData(),
    cta_name: ctaName,
    cta_location: CTA_LOCATION,
  });
};

export type LoyaltyReminderDialogProps = {
  isOpened: boolean;
  title: string;
  subtitle?: string;
  text: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  onPrimaryButtonClick?: () => void;
  onSecondaryButtonClick?: () => void;
  onClose?: () => void;
  className?: string;
  isPrimaryButtonPending?: boolean;
  isSecondaryButtonPending?: boolean;
};

export const LoyaltyReminderDialog: FC<LoyaltyReminderDialogProps> = ({
  isOpened,
  title,
  subtitle,
  text,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  onClose,
  className,
  isPrimaryButtonPending = false,
  isSecondaryButtonPending = false,
}) => {
  const { width } = useViewport();

  const isMobileSmall = width < BREAKPOINT_SMALL_LARGE;

  const handlePrimaryButtonClick = () => {
    dispatchLoyaltyReminderCtaClick(primaryButtonText);
    onPrimaryButtonClick?.();
  };

  const handleSecondaryButtonClick = () => {
    dispatchLoyaltyReminderCtaClick(secondaryButtonText ?? "");
    onSecondaryButtonClick?.();
  };

  useEffect(() => {
    if (isOpened) {
      dispatchTrackingEvent(TrackingEvents.TrackingPageView, {
        ...buildCommonTrackingData(),
      });
    }
  }, [isOpened]);

  const handleCloseDialog = () => {
    dispatchLoyaltyReminderCtaClick(CTA_NAME_POPUP_CLOSE);
    onClose?.();
  };

  const handleTextClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName.toLowerCase() === "a") {
      dispatchLoyaltyReminderCtaClick(target.textContent);
    }
  };

  return (
    <Dialog
      isOpened={isOpened}
      onClose={handleCloseDialog}
      fullscreen={isMobileSmall}
      className={classNames(styles.dialog, className)}
      dataTestId="loyalty-reminder-dialog"
      backgroundBlur={70}
      backgroundColor="rgba(255, 255, 255, 0.5)"
    >
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>{title}</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        <div
          className={styles.text}
          dangerouslySetInnerHTML={{
            __html: sanitize(text),
          }}
          onClick={handleTextClick}
        />
        <div className={styles.buttons}>
          {secondaryButtonText && onSecondaryButtonClick && (
            <Button
              variant="secondary"
              size="lg"
              onClick={handleSecondaryButtonClick}
              className={styles.secondaryButton}
              pending={isSecondaryButtonPending}
              data-testid="desktop-secondary-button"
            >
              {secondaryButtonText}
            </Button>
          )}
          <Button
            variant="primary"
            size="lg"
            onClick={handlePrimaryButtonClick}
            className={styles.primaryButton}
            pending={isPrimaryButtonPending}
            data-testid="desktop-primary-button"
          >
            {primaryButtonText}
          </Button>
        </div>
        <div className={styles.buttonsMobile}>
          <Button
            variant="primary"
            size="sm"
            onClick={onPrimaryButtonClick}
            className={styles.primaryButton}
            pending={isPrimaryButtonPending}
          >
            {primaryButtonText}
          </Button>
          {secondaryButtonText && onSecondaryButtonClick && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSecondaryButtonClick}
              className={styles.secondaryButton}
              pending={isSecondaryButtonPending}
            >
              {secondaryButtonText}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};
